import React, { useMemo, useState } from "react";
import { toast } from "react-toastify";
import moment from "moment";
import ModalMobile from "../../../../../components/shared/Modal/ModalMobile";
import { useUpdateCollection } from "../../../../swingCollections/hooks/useUpdateCollection";
import { useAllSwingCollectionsByUserId } from "../../../../swingCollections/hooks/useAllSwingCollectionsByUserId";
import Button from "../../../../../components/shared/Button/Button";
import { useInvalidate } from "../../../../../hooks/useInvalidate";

export default function AddToCollectionModal({
  isOpen,
  onClose,
  videoIds = [],
}) {
  const { collections } = useAllSwingCollectionsByUserId();
  const updateCollection = useUpdateCollection();
  const invalidate = useInvalidate();

  const [loading, setLoading] = useState({});

  const handleSaveToCollection = async (collectionId) => {
    const collection = collections?.find((c) => c.id === collectionId);
    if (!collection) return;

    try {
      setLoading((prev) => ({ ...prev, [collectionId]: true }));

      // Get existing video IDs and add new ones (avoiding duplicates)
      const existingVideoIds = Array.isArray(collection.videoIds)
        ? collection.videoIds
        : [];
      const newVideoIds = videoIds.filter(
        (id) => !existingVideoIds.includes(id)
      );

      if (newVideoIds.length === 0) {
        toast.error("All swings are already in the collection");
        return;
      }

      const updatedVideoIds = [...existingVideoIds, ...newVideoIds];

      await updateCollection.mutateAsync({
        collectionId: collection.id,
        collectionName: collection.collectionName,
        videoIds: updatedVideoIds,
      });

      toast.success(
        `${newVideoIds.length} ${
          newVideoIds.length === 1 ? "swing" : "swings"
        } added to collection`
      );

      await invalidate(["getAllSwingCollectionsByUserId_key"]);

      onClose();
    } catch (error) {
      const msg = error?.message || "Failed to add swings to collection";
      toast.error(msg);
    } finally {
      setLoading((prev) => ({ ...prev, [collectionId]: false }));
    }
  };

  return (
    <ModalMobile
      className="font-manrope w-full max-w-[812px] overflow-hidden p-0"
      isOpen={isOpen}
      onClose={onClose}
      showClose={true}
    >
      <div className="p-6">
        <div className="text-gray700 mb-6 text-lg font-semibold">
          Save to Existing Collection
        </div>

        {collections && collections.length > 0 ? (
          <div className="border-gray200 max-h-[368px] overflow-y-auto rounded-xl border">
            <table className="w-full table-fixed border-collapse">
              <thead className="bg-gray50 text-left">
                <tr>
                  <th className="px-2 py-3"></th>
                  <th className="px-2 py-3">
                    <span className="text-gray600 text-xs">Collection</span>
                  </th>
                  <th className="px-2 py-3">
                    <span className="text-gray600 text-xs">Swings</span>
                  </th>
                  <th className="px-2 py-3">
                    <span className="text-gray600 text-xs">Created</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {collections.map((collection) => {
                  const swingsCount = Array.isArray(collection.videoIds)
                    ? collection.videoIds.length
                    : 0;
                  const createdAt =
                    collection.createdAt || collection.updatedAt;
                  const isLoading = loading[collection.id] || false;

                  return (
                    <tr
                      className="border-gray200 border-x-0 border-t-0 border-b last:border-b-0"
                      key={collection.id}
                    >
                      <td className="px-2 py-3">
                        <Button
                          className="rounded-lg"
                          color="primary500"
                          isDisabled={isLoading}
                          onClick={() => handleSaveToCollection(collection.id)}
                          size="sm"
                        >
                          {isLoading ? "Saving…" : "Save"}
                        </Button>
                      </td>
                      <td className="px-2 py-3">
                        <span className="text-gray700 text-sm font-medium">
                          {collection.collectionName}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <span className="text-gray700 text-sm">
                          {swingsCount}
                        </span>
                      </td>
                      <td className="px-2 py-3">
                        <span className="text-gray700 text-sm">
                          {moment(createdAt).format("MMM D, YYYY")}
                        </span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        ) : (
          <div className="text-gray500 py-8 text-center">
            No collections available
          </div>
        )}
      </div>
    </ModalMobile>
  );
}
